import {
	apiClient,
	Button,
	ButtonGroup,
	DropdownFormInput,
	parser,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import widgetApi from "../../api/widgetApi";
import getChangeDates from "../../dateUtils";
import { useAuthenticatedAuthContext } from "../auth/Auth.component";
import ZoneSelector from "../inputs/zone-selector/ZoneSelector";

interface FormData {
	timeOfChange: string;
	zoneFrom: string;
	zoneTo: string;
}

const EditZonePage: React.FC = () => {
	const { id } = useParams();
	const startDates = getChangeDates();
	const { tickets, token, userData, refetchSubscriptions } =
		useAuthenticatedAuthContext();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	const ticket = tickets.find((x) => x.id === id);

	if (!ticket) {
		throw new Error(`Could not find subscription with id=${id}`);
	}

	const formMethods = useForm<FormData>({
		defaultValues: {
			timeOfChange: startDates[0],
			zoneFrom: ticket.zoneFrom,
			zoneTo: ticket.zoneTo,
		},
	});

	const editZone = async (formData: FormData) => {
		if (
			formData.zoneFrom === ticket.zoneFrom &&
			formData.zoneTo === ticket.zoneTo
		) {
			formMethods.setError("zoneFrom", {
				message: "Fra sone og/eller til sone må endres.",
				type: "no-change",
			});
			formMethods.setError("zoneTo", {
				message: "Fra sone og/eller til sone må endres.",
				type: "no-change",
			});
			return;
		}

		setLoading(true);

		const response = await apiClient.request(
			widgetApi.tickets.editZone(
				token,
				userData,
				ticket.id,
				parser.date.fromShortDateString(formData.timeOfChange),
				formData.zoneFrom,
				formData.zoneTo,
			),
		);
		if (response.error) {
			setLoading(() => {
				throw response.error;
			});
			return;
		}

		navigate("/abonnement");
		await refetchSubscriptions();
	};

	return (
		<div data-test-id="edit-zone-page">
			<h1>Endre sone</h1>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(editZone)}>
					<DropdownFormInput name="timeOfChange" label="Endringen gjelder fra">
						{startDates.map((date) => (
							<option key={date} value={date}>
								{date}
							</option>
						))}
					</DropdownFormInput>
					<ZoneSelector />

					<ButtonGroup>
						<Button
							data-test-id="confirm-button"
							skin="primary"
							type="submit"
							loading={loading}
						>
							Bekreft
						</Button>
						<Button
							data-test-id="cancel-button"
							skin="cancel"
							onClick={() => {
								navigate("/abonnement");
							}}
							type="button"
						>
							Avbryt
						</Button>
					</ButtonGroup>
				</form>
			</FormProvider>
		</div>
	);
};

export default EditZonePage;
